import {NonLifestyleGoalType} from "../Goals/models/GoalType";
import {BeneficiaryGoals, BeneficiaryGoalsResponse, TaxLiabilities} from "../Goals/models/GoalModelType";
import {FamilyGoalType} from "../Goals/models/FamilyGoalType";
import {LifestylePresentValueResponse} from "../Goals/models/PresentValue";


export type InvestableSufficiencyArguments = {
    nonLifestyleGoals: NonLifestyleGoalType[],
    familyGoals: { [key: string]: BeneficiaryGoals },
    totalInvestableValue: number,
    lifestylePresentValue: LifestylePresentValueResponse,
    taxLiabilities: TaxLiabilities,
    enableEnhancedLifestyleFunding?: boolean
}
export const isInvestablySufficient = (sufficiencyArgs: InvestableSufficiencyArguments): boolean => getNetInvestableValue(sufficiencyArgs) > 0;
export const portfolioReserveIsInvestablySufficient = (sufficiencyArgs: InvestableSufficiencyArguments): boolean => {
    return (getNetInvestableValue(sufficiencyArgs) + sufficiencyArgs.lifestylePresentValue.presentValueForInsufficientYears) > 0;
}

export const investableShortfall = (sufficiencyArgs: InvestableSufficiencyArguments): number => {
    return Math.abs(getNetInvestableValue(sufficiencyArgs) + sufficiencyArgs.lifestylePresentValue.presentValueForInsufficientYears);
}

export const getNetInvestableValue = ({
                                          nonLifestyleGoals,
                                          familyGoals,
                                          totalInvestableValue,
                                          lifestylePresentValue,
                                          taxLiabilities,
                                          enableEnhancedLifestyleFunding
                                      }: InvestableSufficiencyArguments): number => {
    let portfolioAlignedGoalsAndLiabilitiesValue: number = getPortfolioAlignedGoalsAndLiabilitiesValue({
        nonLifestyleGoals,
        familyGoals,
        lifestylePresentValue,
        taxLiabilities,
        enableEnhancedLifestyleFunding
    });
    return (totalInvestableValue - (portfolioAlignedGoalsAndLiabilitiesValue + (enableEnhancedLifestyleFunding ? lifestylePresentValue.fundedByNonInvestable! : lifestylePresentValue.presentValueForInsufficientYears)))
}

export type getPortfolioAlignedGoalsAndLiabilitiesArguments = Omit<InvestableSufficiencyArguments, "totalInvestableValue">;

export const getPortfolioAlignedGoalsAndLiabilitiesValue = ({
                                                                nonLifestyleGoals,
                                                                familyGoals,
                                                                lifestylePresentValue,
                                                                taxLiabilities,
                                                                enableEnhancedLifestyleFunding
                                                            }: getPortfolioAlignedGoalsAndLiabilitiesArguments): number => {
    const getNonLSGoalValue = (goal: NonLifestyleGoalType | FamilyGoalType): number => {
        if (goal.userInputs.isFundedByNonInvestableAssets) {
            return 0;
        }

        if (enableEnhancedLifestyleFunding) {
            return (goal.calculatedFields.presentValueFundedByPortfolio!) + (goal.calculatedFields.investableShortfall!);
        }

        return goal.calculatedFields.presentValue;
    };

    const goalsFundedByInvestablesPresentValue = [
        ...nonLifestyleGoals,
        ...Object.values(familyGoals).flatMap((beneficiaryGoal: BeneficiaryGoalsResponse) => Object.values(beneficiaryGoal.goals))
    ].reduce((total: number, goal: NonLifestyleGoalType | FamilyGoalType) => total + getNonLSGoalValue(goal), 0);

    const lifestylePresentValueToUse = enableEnhancedLifestyleFunding
        ? lifestylePresentValue.requiredPortfolioSpendingPresentValue + lifestylePresentValue.presentValueForInsufficientYears
        : lifestylePresentValue.requiredPortfolioSpendingPresentValue;

    return lifestylePresentValueToUse + goalsFundedByInvestablesPresentValue + taxLiabilities.totalTaxLiabilitiesFundedByPortfolio;
}

export type InflowReserveLengthArguments = {
    portfolioReserveLength: number,
    maxInflowReserveLength: number
}

export const isPortfolioReserveLessThanInflowReserveLength = (inflowReserveArgs: InflowReserveLengthArguments): boolean => {
    return (inflowReserveArgs.maxInflowReserveLength > inflowReserveArgs.portfolioReserveLength);
};